import { ErrorOutline, InfoOutlined } from "@mui/icons-material";
import { Autocomplete, Button, Divider, TextField, Tooltip, Typography } from "@mui/material";
import {
  CompetitorContactsItem,
  ContractTypeOptions,
  MuiFlatpickr,
  RelationshipController,
  TinyMCEController,
  YNOptions,
} from "components/summary/success-plan";
import { SuccessPlanState } from "components/summary/success-plan/state/successPlan.types";
import { useEffect, useRef } from "react";
import { Controller, UseFormReturn, useFieldArray, useWatch } from "react-hook-form";
import styles from "./SuccessPlanWorkflow.module.scss";
import { HighTouchSegmentOptions } from "components/summary/success-plan/constants";

type CustomerDetailStepProps = {
  spState: SuccessPlanState;
  form: UseFormReturn<any>;
};

const CustomerDetailStep = ({ form, spState }: CustomerDetailStepProps) => {
  const rendered = useRef(true);
  const { control, setValue } = form;
  const { spData } = spState;

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "competitorContacts",
  });

  const watched = useWatch({
    control,
    name: ["hasMsftContract", "knowDecMaker"],
  });
  const [hasMsftContract, knowDecMaker] = watched;

  const hasMsftContractFlag = String(hasMsftContract) === "1";
  const isKnowDecMaker = String(knowDecMaker) === "1";

  const appendNewContact = () => {
    append({
      decMakerName: "",
      decMakerTitle: "",
      decMakerSentiment: "",
      competitorFlag: "0",
      competitorDevices: [],
      competitorMtg: [],
      competitorMtgOtherContent: "",
      competitorDevicesOtherContent: "",
    });
  };

  const handleContactsAddMore = () => {
    appendNewContact();
  };

  useEffect(() => {
    if (!hasMsftContractFlag) {
      setValue("contractType", "");
      setValue("msftRenewalDate", "");
    }
  }, [hasMsftContractFlag]);

  useEffect(() => {
    if (!isKnowDecMaker) {
      setValue("decMaker", "");
      setValue("decMakerSentiment", "");
    }
  }, [isKnowDecMaker]);

  useEffect(() => {
    if (rendered.current) {
      rendered.current = false;
      return;
    }
    if (knowDecMaker === "1") {
      if (fields.length === 0) {
        appendNewContact();
      }
    } else {
      replace([]);
    }
  }, [knowDecMaker]);

  useEffect(() => {
    if (
      spData.knowDecMaker === "1" &&
      Array.isArray(spData.competitorContacts) &&
      spData.competitorContacts.length === 0
    ) {
      appendNewContact();
    }
  }, [spData]);

  return (
    <div className={styles["step-wrapper"]}>
      <div className={styles["grid-two-lane"]}>
        <div className={styles["form-field-wrapper"]}>
          <Typography className={styles["form-field-label"]}>High Touch Segmentation</Typography>
          <Tooltip
            title="Advocate / Invested: Activity engaged, health usage, regular engagement cadence. Unresponse / Dormant: Minimal engagement, competitor preference, migration risk."
            className={styles["info-tip"]}
          >
            <InfoOutlined fontSize="small" />
          </Tooltip>
        </div>
        <Controller
          control={control}
          name="highTouchSegment"
          render={({ field }) => (
            <Autocomplete
              {...field}
              disableClearable
              options={HighTouchSegmentOptions}
              value={HighTouchSegmentOptions.find((o) => o.value === field.value)}
              onBlur={field.onBlur}
              onChange={(_, option) => field.onChange(option?.value || "")}
              getOptionLabel={(o: any) => o.label || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  placeholder="Select One"
                  sx={{ width: "240px" }}
                />
              )}
            />
          )}
        />

        <div className={styles["form-field-wrapper"]}>
          <Typography className={styles["form-field-label"]}>
            Does Customer have a Microsoft Contract?
          </Typography>
        </div>
        <Controller
          control={control}
          name="hasMsftContract"
          render={({ field }) => (
            <Autocomplete
              {...field}
              disableClearable
              options={YNOptions}
              value={YNOptions.find((o) => o.value === field.value)}
              onBlur={field.onBlur}
              onChange={(_, option) => field.onChange(option?.value || "")}
              getOptionLabel={(o: any) => o.label || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  placeholder="Select One"
                  sx={{ width: "240px" }}
                />
              )}
            />
          )}
        />

        {hasMsftContractFlag && (
          <>
            <div className={styles["form-field-wrapper"]}>
              <Typography className={styles["form-field-label"]}>What Type of Contract?</Typography>
            </div>
            <Controller
              control={control}
              name="contractType"
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  disableClearable
                  options={ContractTypeOptions}
                  value={ContractTypeOptions.find((o) => o.value === field.value)}
                  onBlur={field.onBlur}
                  onChange={(_, option) => field.onChange(option?.value || "")}
                  getOptionLabel={(o: any) => o.label || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      variant="outlined"
                      placeholder="Select One"
                      sx={{ width: "240px" }}
                    />
                  )}
                />
              )}
            />

            <div className={styles["form-field-wrapper"]}>
              <Typography className={styles["form-field-label"]}>
                Microsoft Contract Renewal Date:
              </Typography>
            </div>

            <Controller
              control={control}
              name={"msftRenewalDate"}
              render={({ field }) => (
                <MuiFlatpickr
                  {...field}
                  value={field.value}
                  onChange={(dates) => field.onChange(dates[0] ?? null)}
                  placeholder="Please select"
                  size="small"
                  sx={{
                    width: "240px",
                    textAlign: "start",
                  }}
                />
              )}
            />
          </>
        )}

        <div className={styles["form-field-wrapper"]}>
          <Typography className={styles["form-field-label"]}>
            What level of relationship do you have with the customer:
          </Typography>
        </div>

        <div className={`${styles["form-field-wrapper"]} `}>
          <RelationshipController
            control={control}
            name="relationships"
          />
        </div>

        <div className={styles["form-field-wrapper"]}>
          <Typography className={styles["form-field-label"]}>
            Do you know who the decision maker is?
          </Typography>
        </div>
        <Controller
          control={control}
          name="knowDecMaker"
          render={({ field }) => (
            <Autocomplete
              {...field}
              disableClearable
              options={YNOptions}
              value={YNOptions.find((o) => o.value === field.value)}
              onBlur={field.onBlur}
              onChange={(_, option) => field.onChange(option?.value)}
              getOptionLabel={(o: any) => o.label || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  placeholder="Select One"
                  sx={{ width: "240px" }}
                />
              )}
            />
          )}
        />

        {isKnowDecMaker && (
          <>
            <div
              className={styles["form-field-wrapper"]}
              style={{ flexDirection: "column", alignItems: "start" }}
            >
              <Typography className={styles["form-field-label"]}>
                Outside of your primary contact, are there other decision makers or advocated of
                competitors we should be aware of?
              </Typography>
              <Button
                disableRipple
                disableTouchRipple
                disableFocusRipple
                sx={{
                  textTransform: "none",
                  ":hover": { backgroundColor: "transparent" },
                  fontSize: "12px",
                }}
                onClick={handleContactsAddMore}
              >
                Add More
              </Button>
            </div>
            <div>
              {fields.map((item, index) => (
                <div key={item.id}>
                  <CompetitorContactsItem
                    form={form}
                    item={item}
                    index={index}
                    onRemoveCallback={(index) => remove(index)}
                  />
                  {index !== fields.length - 1 && <Divider sx={{ my: 2 }} />}
                </div>
              ))}
            </div>
          </>
        )}

        <div className={`${styles["flex-vertical-gap"]} ${styles["grid-column-span-2"]}`}>
          <div className={styles["form-field-wrapper"]}>
            <Typography className={styles["form-field-label"]}>
              Are there any recent executive changes(last 18 months)?
            </Typography>
          </div>
          <TinyMCEController
            name="recentExecutive"
            control={control}
          />
        </div>

        {/* <div className={styles["form-field-wrapper"]}>
          <Typography className={styles["form-field-label"]}>
            What is the level of customer responsiveness?
          </Typography>
        </div>
        <Controller
          control={control}
          name="responsiveLvl"
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              autoComplete="off"
              fullWidth
              error={!!fieldState.error}
              InputProps={{
                endAdornment: !!fieldState.error ? (
                  <Tooltip title={fieldState.error.message}>
                    <ErrorOutline color="error" fontSize="small" />
                  </Tooltip>
                ) : null,
              }}
            />
          )}
          rules={{
            maxLength: {
              value: 60,
              message: "Maximum 60 characters",
            },
          }}
        /> */}
      </div>
    </div>
  );
};

export default CustomerDetailStep;
